import { useClient } from "../composables/useClient"
import { AppErrors, AppError, handleAppError } from "../utils/error"
import type { CreateUser } from "../utils/types"
import { UserSchema, JourneyDataSchema } from "../utils/types"

export const fetchJourney = async () => {
  const startTime = performance.now()
  console.log(`[PERF] 🔄 fetchJourney() started`)

  const { GET } = useClient()
  const journeyStore = useJourneyStore()

  try {
    const getStart = performance.now()
    const { data, error } = await GET("/api/journey/me")
    console.log(
      `[PERF] ⏱️  GET /api/journey/me took ${(performance.now() - getStart).toFixed(2)}ms`
    )

    if (error) {
      throw new AppError(AppErrors.FetchJourneyRequest)
    }

    const validation = JourneyDataSchema.safeParse(data)
    if (!validation.success) {
      console.log(validation.error.message)
      throw new AppError(AppErrors.FetchJourneyResponse)
    }

    journeyStore.setJourneyData(validation.data)
    console.log(
      `[PERF] ✅ fetchJourney() completed in ${(performance.now() - startTime).toFixed(2)}ms`
    )
  } catch (error) {
    console.log(
      `[PERF] ❌ fetchJourney() failed after ${(performance.now() - startTime).toFixed(2)}ms`
    )
    handleAppError(error, new AppError(AppErrors.FetchJourney))
  }
}

export const fetchUser = async () => {
  const { GET } = useClient()
  const userStore = useUserStore()

  try {
    if (userStore.id && userStore.token) {
      const { data, error } = await GET("/api/journey/users/{id}", {
        params: {
          path: {
            id: userStore.id
          }
        }
      })

      if (error) {
        throw new AppError(AppErrors.ValidateUserRequest)
      }

      const validation = UserSchema.safeParse(data)
      if (!validation.success) {
        console.log(validation.error.message)
        throw new AppError(AppErrors.ValidateUserResponse)
      }

      userStore.setUser(validation.data.id, validation.data.token)
    } else {
      throw new AppError(AppErrors.ValidateUserStoreEmpty)
    }
  } catch (error) {
    handleAppError(error, new AppError(AppErrors.ValidateUser))
  }
}

const createUser = async (
  ticketId: string,
  quest_name: string = "",
  story_name: string = ""
) => {
  const { POST } = useClient()
  const userStore = useUserStore()

  try {
    const requestBody: CreateUser = {
      user: {
        ticket_id: ticketId,
        quest_name: quest_name,
        story_name: story_name
      }
    }
    const { data, error } = await POST("/api/journey/users", {
      body: requestBody
    })

    if (error) {
      throw new AppError(AppErrors.CreateUserRequest)
    }

    const validation = UserSchema.safeParse(data)
    if (!validation.success) {
      console.log(validation.error.message)
      throw new AppError(AppErrors.CreateUserResponse)
    }

    userStore.setUser(validation.data.id, validation.data.token)
  } catch (error) {
    handleAppError(error, new AppError(AppErrors.CreateUser))
  }
}

export const updateUser = async (userData: {
  first_name: string
  last_name: string
  email: string
}) => {
  const { PUT } = useClient()
  const userStore = useUserStore()

  try {
    const requestBody = { user: userData }
    const { data, error } = await PUT("/api/journey/users", {
      body: requestBody
    })

    if (error) {
      throw new AppError(AppErrors.UpdateUserRequest)
    }

    const validation = UserSchema.safeParse(data)
    if (!validation.success) {
      console.log(validation.error.message)
      throw new AppError(AppErrors.UpdateUserResponse)
    }

    userStore.setUser(validation.data.id, validation.data.token)
  } catch (error) {
    handleAppError(error, new AppError(AppErrors.UpdateUser))
  }
}

export const handleTicket = async (options?: {
  questName?: string
  storyName?: string
}) => {
  const search = window.location.search
  const searchParams = new URLSearchParams(search)
  const ticketId = searchParams.get("ticket_id")

  if (ticketId) {
    await createUser(ticketId)
  } else if (options?.questName || options?.storyName) {
    await createUser(
      "without_ticket",
      options.questName ?? "",
      options.storyName ?? ""
    )
  }
}
